import React from "react"

/**
 * Keyboard shortcuts for the Modern record page.
 *
 * Ported from the prototype's `pages/Record/index.js` key handler, including
 * its calipers (`C`) and sidebar (`S`) bindings now that both features exist.
 */
export type RecordShortcutHandlers = {
    /** H */
    onHome(): void
    /** 1 – 6 */
    onInterpretation(index: number): void
    /** D */
    onToggleTheme(): void
    /** [ */
    onTogglePanel(): void
    /** O */
    onClinicalInfo(): void
    /** C */
    onToggleCalipers(): void
    /** S */
    onToggleSidebar(): void
    /** + or = */
    onZoomIn(): void
    /** - */
    onZoomOut(): void
    /** 0 */
    onZoomReset(): void
    /** Escape */
    onEscape(): void
    /** Ctrl/Cmd + S */
    onSave(): void
    /** Ctrl/Cmd + Enter */
    onApprove(): void
    onPage(direction: -1 | 1): void
    onPageEnd(direction: -1 | 1): void
}

const PHYSICAL_KEYS: {[code: string]: string} = {
    KeyH: "h",
    KeyC: "c",
    KeyS: "s",
    KeyD: "d",
    KeyO: "o",
    Digit0: "0", Numpad0: "0",
    Digit1: "1", Numpad1: "1",
    Digit2: "2", Numpad2: "2",
    Digit3: "3", Numpad3: "3",
    Digit4: "4", Numpad4: "4",
    Digit5: "5", Numpad5: "5",
    Digit6: "6", Numpad6: "6",
    BracketLeft: "[",
    Equal: "+", NumpadAdd: "+",
    Minus: "-", NumpadSubtract: "-",
};

function bindingFor(event: KeyboardEvent): string {
    const code = event.code;

    if (typeof code === "string" && typeof PHYSICAL_KEYS[code] === "string") {
        return PHYSICAL_KEYS[code]
    }

    return (event.key || "").toLowerCase()
}

/**
 * True while the user is typing. Without this the conclusion textarea swallows
 * every plain-letter binding — typing "h" would navigate to the dashboard
 * mid-sentence.
 */
function isTypingTarget(target: EventTarget | null): boolean {
    if (target === null || typeof (target as HTMLElement).tagName === "undefined") {
        return false
    }

    const element = target as HTMLElement;
    const tag = (element.tagName || "").toUpperCase();

    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
        return true
    }

    return element.isContentEditable === true
}

/**
 * True while ANY modal dialog is mounted (ApproveDialog, InitDialog,
 * InactivityWarningDialog, ModernClinicalDialog). Those are modal: while one is
 * up, the page behind it must not respond to keys at all.
 *
 * Queries the document rather than walking up from `event.target`, which is
 * what the previous version did and which never matched. MUI v4 puts
 * `role="dialog"` on the dialog's *Paper*, but `TrapFocus` moves focus to the
 * container div that WRAPS it (`role="none presentation"`), so the keydown
 * target was a sibling-ancestor of the `role="dialog"` node and the parent walk
 * always returned false. The result: pressing "4" while the approve
 * confirmation was open silently rewrote the interpretation the reviewer was
 * confirming, and "h" navigated away mid-approval. ModernClinicalDialog was
 * worse — it does no focus management, so the target stayed on <body>.
 *
 * Each dialog owns its own Escape handling, so swallowing Escape here too is
 * correct.
 */
function isDialogOpen(): boolean {
    if (typeof document === "undefined" || !document.querySelector) {
        return false
    }

    return document.querySelector("[role=\"dialog\"]") !== null
}

/**
 * @param handlers
 * @param options `enabled: false` unbinds the whole set. `bareKeysOnly: false`
 *     keeps just the modifier combinations — Ctrl+S and Ctrl+Enter — and drops
 *     every bare letter, digit and arrow. That is what annotation mode wants:
 *     its own keyboard claims `+`, `-`, `[`, `o`, the arrows and `1`-`6`, so
 *     leaving those bound fires two unrelated actions per keystroke, while save
 *     and approve collide with nothing and should still work with their buttons
 *     hidden.
 */
export default function useRecordShortcuts(
    handlers: RecordShortcutHandlers,
    options?: {enabled?: boolean, bareKeys?: boolean},
) {
    const enabled = options ? options.enabled !== false : true;
    const bareKeysDisabled = options ? options.bareKeys === false : false;

    // Keep the latest closures in a ref so the window listener is bound exactly
    // once. Re-binding on every render would be a new listener per keystroke of
    // the conclusion textarea.
    const handlersRef = React.useRef<RecordShortcutHandlers>(handlers);

    React.useEffect(() => {
        handlersRef.current = handlers
    });

    React.useEffect(() => {
        if (!enabled) {
            return
        }

        function onKeyDown(event: KeyboardEvent) {
            if (isDialogOpen()) {
                return
            }

            // Escape and Enter are matched on `key`, not `code`, on purpose:
            // named keys report identically on every layout, and `key` also
            // folds in the numpad's Enter, which `code` would not.
            if (event.key === "Escape") {
                handlersRef.current.onEscape();

                return
            }

            const binding = bindingFor(event);

            if (binding === "") {
                return
            }
            const withModifier = (event.ctrlKey || event.metaKey) && !event.altKey;

            // Save and approve stay live while typing — the whole point of
            // Ctrl+S is to flush the textarea you are currently in.
            if (withModifier && binding === "s") {
                event.preventDefault();
                handlersRef.current.onSave();

                return
            }

            if (withModifier && event.key === "Enter") {
                event.preventDefault();
                handlersRef.current.onApprove();

                return
            }

            if (withModifier || event.altKey) {
                return
            }

            /*
             * Everything past here is a bare letter, digit or arrow, and those
             * are the ones annotation mode claims for itself. Save and approve
             * are above this line because they are modifier combinations that
             * nothing else wants, so they keep working while the toolbar's
             * buttons for them are hidden.
             */
            if (bareKeysDisabled) {
                return
            }

            if (isTypingTarget(event.target)) {
                return
            }

            switch (event.key) {
                case "ArrowLeft":
                    handlersRef.current.onPage(-1);

                    return;
                case "ArrowRight":
                    handlersRef.current.onPage(1);

                    return;
                case "Home":
                    handlersRef.current.onPageEnd(-1);

                    return;
                case "End":
                    handlersRef.current.onPageEnd(1);

                    return;
                default:
                    break
            }

            if (binding >= "1" && binding <= "6" && binding.length === 1) {
                handlersRef.current.onInterpretation(Number(binding));

                return
            }

            switch (binding) {
                case "h":
                    handlersRef.current.onHome();
                    break;
                case "d":
                    handlersRef.current.onToggleTheme();
                    break;
                case "o":
                    handlersRef.current.onClinicalInfo();
                    break;
                case "c":
                    handlersRef.current.onToggleCalipers();
                    break;
                case "s":
                    handlersRef.current.onToggleSidebar();
                    break;
                case "[":
                    handlersRef.current.onTogglePanel();
                    break;
                case "+":
                case "=":
                    handlersRef.current.onZoomIn();
                    break;
                case "-":
                case "_":
                    handlersRef.current.onZoomOut();
                    break;
                // The digit guard above only claims 1-6, so 0 is free.
                case "0":
                    handlersRef.current.onZoomReset();
                    break;
                default:
                    break
            }
        }

        window.addEventListener("keydown", onKeyDown);

        return () => window.removeEventListener("keydown", onKeyDown)
    }, [enabled, bareKeysDisabled]);
}
